import { useEffect, useState } from 'react';
import { Briefcase } from 'lucide-react';
import { fmtMoney } from '@/lib/domain/format';
import { useDealDrawer } from '@/features/pipeline/dealDrawerStore';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { useHandoff } from './handoffStore';
import { type HandoffForm, buildHandoffBody, parseDecimal, useHandoffDeal } from './useHandoffDeal';

/** number → string para o input de cheque ("" quando nulo). */
const moneyToInput = (n: number | null): string => (n == null ? '' : String(n));

const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';
const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink';

/**
 * Modal de handoff ("Mover para Portfólio"). Acionado pela ficha do Deal. Cria o Investment via
 * POST /api/deals/{id}/handoff (If-Match). Herda empresa/cheque/1ª tese do Deal (não editáveis aqui);
 * o corpo editável é codinome, Fundo (obrigatório), responsável, equipe, MOIC esp. e TIR esp.
 * (DOCUMENTACAO.md §5.8). Aviso "fase ≠ Investimento" é não-bloqueante e mostrado aqui.
 */
export function HandoffModal() {
  const deal = useHandoff((s) => s.deal);
  const close = useHandoff((s) => s.close);
  const closeDeal = useDealDrawer((s) => s.closeDeal);
  const reference = useReferenceData();
  const handoff = useHandoffDeal();

  const [form, setForm] = useState<HandoffForm>({
    codename: '',
    moicExpected: '',
    irrExpected: '',
    fundId: null,
    leadUserId: '',
    teamUserIds: [],
    checkDiscretionary: '',
    checkCoinvestment: '',
  });

  // Pré-preenche codinome, responsável e split do cheque a partir do Deal a cada abertura.
  // Split: usa o do Deal quando há (item 3); senão Discricionário = valor do cheque e Coinvest. = 0.
  useEffect(() => {
    if (deal) {
      const hasSplit = deal.checkDiscretionaryBRL != null || deal.checkCoinvestmentBRL != null;
      setForm({
        codename: deal.codename,
        moicExpected: '',
        irrExpected: '',
        fundId: null,
        leadUserId: deal.leadUserId,
        teamUserIds: [],
        checkDiscretionary: hasSplit ? moneyToInput(deal.checkDiscretionaryBRL) : moneyToInput(deal.investmentValueBRL),
        checkCoinvestment: hasSplit ? moneyToInput(deal.checkCoinvestmentBRL) : '0',
      });
    }
  }, [deal]);

  // Esc fecha o modal (consistente com a ficha do Deal e do Investimento).
  useEffect(() => {
    if (!deal) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deal, close]);

  if (!deal) return null;

  const users = reference.data?.users ?? [];
  const funds = reference.data?.funds ?? [];
  const notInInvestmentStage = deal.stage !== 'Investimento';

  const checkDiscretionary = parseDecimal(form.checkDiscretionary) ?? 0;
  const checkCoinvestment = parseDecimal(form.checkCoinvestment) ?? 0;
  const checkTotal = checkDiscretionary + checkCoinvestment;

  const codenameOk = form.codename.trim().length > 0;
  const fundOk = form.fundId != null;
  const leadOk = form.leadUserId !== '';
  const checkOk = checkTotal > 0;
  const canConfirm = codenameOk && fundOk && leadOk && checkOk && !handoff.isPending;

  const toggleTeam = (id: string) =>
    setForm((f) => ({
      ...f,
      teamUserIds: f.teamUserIds.includes(id)
        ? f.teamUserIds.filter((x) => x !== id)
        : [...f.teamUserIds, id],
    }));

  const onConfirm = () => {
    if (!canConfirm) return;
    handoff.mutate(
      { id: deal.id, etag: deal.etag, body: buildHandoffBody(form) },
      {
        onSuccess: () => {
          close();
          closeDeal();
        },
      },
    );
  };

  return (
    <>
      <button
        type="button"
        aria-label="Cancelar"
        onClick={close}
        className="fixed inset-0 z-[70] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label="Mover para o Portfólio"
        className="fixed left-1/2 top-1/2 z-[80] max-h-[90vh] w-[min(520px,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-vega-surface p-5 shadow-vega-lg"
      >
        <div className="mb-1 flex items-center gap-2">
          <Briefcase size={16} className="text-vega-ink-2" />
          <h2 className="text-[15px] font-semibold text-vega-ink">Mover para o Portfólio</h2>
        </div>
        <p className="mb-1 text-[12px] text-vega-muted">
          {deal.code} · {deal.companyName}
        </p>
        <p className="mb-4 text-[12.5px] text-vega-muted-2">
          Cria o investimento herdando empresa e a tese principal do deal; informe o cheque
          (discricionário + coinvestimento). O deal sai do funil (fechado para o portfólio).
        </p>

        {notInInvestmentStage && (
          <div className="mb-4 rounded border border-vega-warn/40 bg-vega-warn-soft px-3 py-2 text-[12px] text-vega-warn">
            Este deal ainda não está na fase Investimento. Você pode movê-lo mesmo assim.
          </div>
        )}

        <label className="mb-3 block">
          <span className={LABEL}>Codinome *</span>
          <input
            value={form.codename}
            onChange={(e) => setForm((f) => ({ ...f, codename: e.target.value }))}
            maxLength={200}
            autoFocus
            className={INPUT}
          />
        </label>

        <label className="mb-3 block">
          <span className={LABEL}>Fundo *</span>
          <select
            value={form.fundId ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, fundId: e.target.value === '' ? null : Number(e.target.value) }))}
            className={INPUT}
          >
            <option value="">Selecione…</option>
            {funds.map((fund) => (
              <option key={fund.id} value={fund.id}>
                {fund.name}
              </option>
            ))}
          </select>
          {reference.isLoading && <span className="mt-1 block text-[11px] text-vega-muted-2">Carregando fundos…</span>}
          {!reference.isLoading && funds.length === 0 && (
            <span className="mt-1 block text-[11px] text-vega-danger">
              Nenhum fundo cadastrado. Cadastre um fundo em Configurações antes de mover.
            </span>
          )}
        </label>

        <label className="mb-3 block">
          <span className={LABEL}>Responsável *</span>
          <select
            value={form.leadUserId}
            onChange={(e) => setForm((f) => ({ ...f, leadUserId: e.target.value }))}
            className={INPUT}
          >
            <option value="">Selecione…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>

        <div className="mb-3">
          <span className={LABEL}>Equipe</span>
          <div className="max-h-32 overflow-y-auto rounded border border-vega-line p-1">
            {users
              .filter((u) => u.id !== form.leadUserId)
              .map((u) => {
                const checked = form.teamUserIds.includes(u.id);
                return (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => toggleTeam(u.id)}
                    className="flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-[12.5px] hover:bg-vega-bg-2"
                  >
                    <span
                      className={`inline-flex h-[13px] w-[13px] items-center justify-center rounded-[2px] border ${
                        checked ? 'border-vega-ink bg-vega-ink' : 'border-vega-line'
                      }`}
                    >
                      {checked && <span className="h-1.5 w-1.5 rounded-[1px] bg-white" />}
                    </span>
                    <span className="flex-1">{u.name}</span>
                  </button>
                );
              })}
          </div>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>Cheque discricionário (R$)</span>
            <input
              value={form.checkDiscretionary}
              onChange={(e) => setForm((f) => ({ ...f, checkDiscretionary: e.target.value }))}
              inputMode="decimal"
              placeholder="ex.: 180000000"
              className={INPUT}
            />
          </label>
          <label className="block">
            <span className={LABEL}>Cheque coinvestimento (R$)</span>
            <input
              value={form.checkCoinvestment}
              onChange={(e) => setForm((f) => ({ ...f, checkCoinvestment: e.target.value }))}
              inputMode="decimal"
              placeholder="ex.: 0"
              className={INPUT}
            />
          </label>
        </div>

        <div className="mb-4 flex items-baseline justify-between rounded border border-vega-line-2 bg-vega-surface-2 px-3 py-2">
          <span className={`${LABEL} mb-0`}>Cheque total</span>
          <span className="font-mono text-[15px] font-semibold text-vega-ink">{fmtMoney(checkTotal)}</span>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className={LABEL}>MOIC esp.</span>
            <input
              value={form.moicExpected}
              onChange={(e) => setForm((f) => ({ ...f, moicExpected: e.target.value }))}
              inputMode="decimal"
              placeholder="ex.: 2,1"
              className={INPUT}
            />
          </label>
          <label className="block">
            <span className={LABEL}>TIR esp. (%)</span>
            <input
              value={form.irrExpected}
              onChange={(e) => setForm((f) => ({ ...f, irrExpected: e.target.value }))}
              inputMode="decimal"
              placeholder="ex.: 22"
              className={INPUT}
            />
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={handoff.isPending}
            className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
          >
            {handoff.isPending ? 'Movendo…' : 'Mover para o Portfólio'}
          </button>
        </div>
      </div>
    </>
  );
}
